import 'bootstrap/dist/css/bootstrap.min.css';
import { FaRegPlusSquare, FaInfoCircle, FaRobot, FaCogs, FaUserCheck, FaTimes } from 'react-icons/fa';
import { Handle, Position, useStore } from 'reactflow';
import SATooltip from '../common/SATooltip';
import { useState, useRef, useEffect } from 'react';
import TaskEngineIcon from '../agents/TaskEngineIcon';
import { showNotificationGlobal } from '../common/NotificationProvider';
import { APPROVAL_CHANNEL_META } from './approvalChannels';

const isInteractiveSelector = (s) =>
  s.nodesDraggable || s.nodesConnectable || s.elementsSelectable;

export default function TaskNode({ data, id, xPos, yPos, selected, onChange = () => {}, onConfigNodeAdd = () => {}, connectedConfig }) {
  const [isHovered, setIsHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showApprovalView, setShowApprovalView] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [highlightColor, setHighlightColor] = useState('green');
  const [statusTooltip, setStatusTooltip] = useState(null); // { status: 'FAILED' | 'SUCCESS', reason: string }
  const dropdownRef = useRef(null);
  const approvalViewRef = useRef(null);
  
  // Check if tools are connected - connectedConfig.tool is an array
  const connectedTools = data?.connectedConfig?.tool || [];
  const hasTool = Array.isArray(connectedTools) ? connectedTools.length > 0 : false;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    // Add event listener with a small delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showApprovalView && approvalViewRef.current && !approvalViewRef.current.contains(event.target)) {
        setShowApprovalView(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showApprovalView]);

  // Handle task run/stop events
  useEffect(() => {
    function handleTaskRun(e) {
      // Only set running if this is our node - explicitly stop all others
      if (e.detail.nodeId === id) {
        setIsRunning(true);
      } else {
        // If a different task is running, ensure this one is stopped
        setIsRunning(false);
      }
    }

    function handleTaskStop(e) {
      if (e.detail.nodeId === id) {
        setIsRunning(false);
      }
    }

    function handleTaskStopAll() {
      // Stop all tasks when stop-all event is dispatched
      setIsRunning(false);
    }

    window.addEventListener('task-run', handleTaskRun);
    window.addEventListener('task-stop', handleTaskStop);
    window.addEventListener('task-stop-all', handleTaskStopAll);
    
    return () => {
      window.removeEventListener('task-run', handleTaskRun);
      window.removeEventListener('task-stop', handleTaskStop);
      window.removeEventListener('task-stop-all', handleTaskStopAll);
    };
  }, [id]);

  // Handle node highlighting events
  useEffect(() => {
    function handleHighlight(e) {
      if (e.detail.nodeId === id && e.detail.nodeType === 'task') {
        setIsHighlighted(true);
        setHighlightColor(e.detail.color || 'green');
        // Also set running state for green highlights
        if (e.detail.color !== 'red') {
          setIsRunning(true);
        }
      }
    }

    function handleStopHighlight() {
      setIsHighlighted(false);
      setIsRunning(false); // Also clear running state when stopping highlights
    }

    window.addEventListener('node-highlight', handleHighlight);
    window.addEventListener('node-highlight-stop-all', handleStopHighlight);
    
    return () => {
      window.removeEventListener('node-highlight', handleHighlight);
      window.removeEventListener('node-highlight-stop-all', handleStopHighlight);
    };
  }, [id]);

  // Handle status tooltip events
  useEffect(() => {
    function handleStatusTooltip(e) {
      if (e.detail.nodeId === id && e.detail.nodeType === 'task') {
        setStatusTooltip({
          status: e.detail.status,
          reason: e.detail.reason
        });
      }
    }

    function handleClearStatusTooltip() {
      setStatusTooltip(null);
    }

    window.addEventListener('node-status-tooltip', handleStatusTooltip);
    window.addEventListener('workflow-stop', handleClearStatusTooltip);
    window.addEventListener('workflow-run', handleClearStatusTooltip);
    
    return () => {
      window.removeEventListener('node-status-tooltip', handleStatusTooltip);
      window.removeEventListener('workflow-stop', handleClearStatusTooltip);
      window.removeEventListener('workflow-run', handleClearStatusTooltip);
    };
  }, [id]);

  const isInteractive = useStore(isInteractiveSelector);

  // Handler for delete button
  const handleDelete = (e) => {
    e.stopPropagation();
    if (!isInteractive){
      showNotificationGlobal('Cannot delete this component because the workflow is locked.', 'error', 'bottomRight');
      return;
    }
    window.dispatchEvent(new CustomEvent('delete-node', {
      detail: { nodeId: id, nodeType: 'task' }
    }));
  };

  // Handler for add agent button
  const handleAddAgent = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    // Find the node's DOM position (fallback to data if not available)
    const nodeRect = e.target.closest('.node-layout')?.getBoundingClientRect();
    const position = nodeRect
      ? { x: nodeRect.right, y: nodeRect.top + nodeRect.height / 2 }
      : { x: xPos || 0, y: yPos || 0 };
    window.dispatchEvent(new CustomEvent('add-agent-from-task', {
      detail: { taskNodeId: id, position }
    }));
  };

  // Handler for add task button
  const handleAddTask = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    // Find the node's DOM position (fallback to data if not available)
    const nodeRect = e.target.closest('.node-layout')?.getBoundingClientRect();
    const position = nodeRect
      ? { x: nodeRect.right, y: nodeRect.top + nodeRect.height / 2 }
      : { x: xPos || 0, y: yPos || 0 };
    window.dispatchEvent(new CustomEvent('add-task-from-task', {
      detail: { taskNodeId: id, position }
    }));
  };

  // Handler for add output button
  const handleAddOutput = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    // Find the node's DOM position (fallback to data if not available)
    const nodeRect = e.target.closest('.node-layout')?.getBoundingClientRect();
    const position = nodeRect
      ? { x: nodeRect.right, y: nodeRect.top + nodeRect.height / 2 }
      : { x: xPos || 0, y: yPos || 0 };
    window.dispatchEvent(new CustomEvent('add-output-from-task', {
      detail: { taskNodeId: id, position }
    }));
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isInteractive) return;
    setShowDropdown(prev => !prev);
  };

  // Handler for add tool button
  const handleAddTool = (e) => {
    e.stopPropagation();
    // Dispatch event to open tool modal in NodePalette
    window.dispatchEvent(new CustomEvent('open-tool-modal', {
      detail: { agentNodeId: id }
    }));
  };

  // Simple color scheme - teal theme
  const primaryColor = '#ccb16d'; // teal-500
  const approvalChannelId = data?.approvalChannelType || data?.approvalChannels?.[0]?.channel || '';
  const approvalChannelMeta = APPROVAL_CHANNEL_META[approvalChannelId] || null;
  const approvalApp = Array.isArray(data?.approvalChannels) ? data.approvalChannels[0] : null;
  const approvalRecipient = data?.approvalRecipient || '';

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 20px ${isHighlighted && highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
            50% {
              box-shadow: 0 0 30px ${isHighlighted && highlightColor === 'red' ? 'rgba(220, 53, 69, 0.8)' : 'rgba(40, 167, 69, 0.8)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
            100% {
              box-shadow: 0 0 20px ${isHighlighted && highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes approvalBadgePulse {
            0%, 100% {
              box-shadow: 0 2px 8px rgba(22, 163, 74, 0.18);
            }
            50% {
              box-shadow: 0 2px 12px rgba(22, 163, 74, 0.32);
            }
          }
          @keyframes approvalCardIn {
            from { opacity: 0; transform: translateY(6px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}
      </style>
      <div
        style={{
          background: '#fff',
          border: isHighlighted 
            ? `2px solid ${highlightColor === 'red' ? '#dc3545' : '#28a745'}` 
            : isRunning 
              ? '2px solid #28a745' 
              : selected 
                ? '1.5px solid rgb(204 177 109)' 
                : '1px solid rgb(204 177 109)',
          borderRadius: 14,
          boxShadow: isHighlighted 
            ? `0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08)` 
            : isRunning 
              ? '0 0 20px rgba(40, 167, 69, 0.6), 0 2px 8px rgba(0,0,0,0.08)' 
              : selected 
                ? 'rgba(0, 0, 0, 0.08) 0px 1px 0px 7px' 
                : '0 2px 8px rgba(0,0,0,0.08)',
          minWidth: 200,
          maxWidth: 200,
          padding: '4px 12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          animation: (isHighlighted || isRunning) ? 'pulse 1.5s ease-in-out infinite' : 'none',
        }}
        title={data.title || 'Task Agent'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {data?.humanApproval && (
          <div ref={approvalViewRef} className="nodrag nopan" style={{ position: 'absolute', top: -14, left: 10, zIndex: 12 }}>
            <button
              type="button"
              title="View human approval"
              onClick={(event) => {
                event.stopPropagation();
                setShowApprovalView((open) => !open);
              }}
              onMouseDown={(event) => event.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 24,
                padding: '0 8px 0 4px',
                border: '1px solid #bbf7d0',
                borderRadius: 999,
                background: 'linear-gradient(180deg, #ecfdf5 0%, #dcfce7 100%)',
                color: '#166534',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.18)',
                animation: showApprovalView ? 'none' : 'approvalBadgePulse 2s ease-in-out infinite',
              }}
            >
              <span style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <FaUserCheck size={12} />
              </span>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.2 }}>Approval</span>
            </button>

            {showApprovalView && (
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: 30,
                  left: 0,
                  width: 228,
                  background: '#fff',
                  border: '1px solid #dcfce7',
                  borderRadius: 14,
                  boxShadow: '0 14px 36px rgba(15, 23, 42, 0.16)',
                  overflow: 'hidden',
                  animation: 'approvalCardIn 0.16s ease-out',
                  textAlign: 'left',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 10px 8px',
                  background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  <span style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <FaUserCheck size={13} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#14532d' }}>Human approval</div>
                    <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.35, marginTop: 2 }}>
                      This agent pauses until a person approves.
                    </div>
                  </div>
                  <button
                    type="button"
                    title="Close"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowApprovalView(false);
                    }}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: 2,
                      display: 'inline-flex',
                    }}
                  >
                    <FaTimes size={10} />
                  </button>
                </div>

                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 9px',
                    borderRadius: 10,
                    background: '#f8fafc',
                    border: '1px solid #eef2f7',
                  }}>
                    {approvalChannelMeta?.icon ? (
                      <img src={approvalChannelMeta.icon} alt="" width={16} height={16} style={{ objectFit: 'contain' }} />
                    ) : (
                      <FaUserCheck size={12} style={{ color: '#16a34a' }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Channel</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                        {approvalChannelMeta?.label || 'Not configured'}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    padding: '8px 9px',
                    borderRadius: 10,
                    background: '#f8fafc',
                    border: '1px solid #eef2f7',
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Connection</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {approvalApp?.name || 'No OAuth selected'}
                    </div>
                  </div>

                  {approvalChannelId === 'slack' && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      padding: '8px 9px',
                      borderRadius: 10,
                      background: '#f8fafc',
                      border: '1px solid #eef2f7',
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Recipient</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', wordBreak: 'break-all' }}>
                        {approvalRecipient || 'Not set'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Simple Icon */}

        <TaskEngineIcon />

        {/* Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 6,
          width: '100%'
        }}>
          <div style={{ 
            fontWeight: 700, 
            fontSize: 14, 
            textAlign: 'center'
          }} title="">
            {data.title || 'Task Agent'}
          </div>
          {statusTooltip && (
            <SATooltip title={statusTooltip.reason} placement="top">
              <FaInfoCircle 
                size={14} 
                style={{ 
                  color: statusTooltip.status === 'FAILED' ? '#dc3545' : '#28a745',
                  cursor: 'pointer',
                  flexShrink: 0
                }} 
              />
            </SATooltip>
          )}
        </div>
        
        {/* Subtitle */}
        <div
          style={{
            fontSize: 11,
            color: '#888',
            textAlign: 'center',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px',
            marginTop: 2,
          }}
          title=""
        >
          {data.subtitle || 'Execute Tasks'}
        </div>

        <Handle type="target" position={Position.Left} id='taskInput' style={{ background: primaryColor, left: -8, width: 13, height: 13 }} title="" />
        <Handle type="source" position={Position.Right} id='taskOutput' style={{ background: primaryColor, right: -8, width: 13, height: 13  }} title="" />
        
        {/* Bottom handle for tool */}
        <Handle type="source" position={Position.Bottom} id="tool" style={{ left: 98, background: primaryColor, bottom: -8, width: 13, height: 13  }} title="" />
        
        {/* Tool Icon */}
        <span
          style={{ position: 'relative', color: hasTool ? '#1976d2' : '#ccc', cursor: 'pointer' }}
        >
          <FaCogs size={18} />
        </span>

        {/* Show dropdown button - hidden if hasConnection */}
        {!data.hasConnection && (
        <div 
          style={{
            position: 'absolute',
            right: -76,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            zIndex: 2
          }}
        >
          <div style={{
            width: 38,
            height: 2,
            background: primaryColor
          }} />
          
          {/* Dropdown Button */}
          <div 
            ref={dropdownRef}
            style={{
              position: 'relative',
              display: 'inline-block'
            }}
          >
            {/* Invisible connect handle over the plus button area (keeps design unchanged). */}
            <Handle
              type="source"
              position={Position.Right}
              id="taskPlusConnect"
              onClick={toggleDropdown}
              style={{
                position: 'absolute',
                inset: 0,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                opacity: 0,
                zIndex: 4,
                cursor: isInteractive ? 'crosshair' : 'default'
              }}
              title=""
            />
            <button
              onClick={toggleDropdown}
              disabled={data.hasConnection}
              style={{
                background: data.hasConnection 
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: data.hasConnection ? 'not-allowed' : 'pointer',
                boxShadow: data.hasConnection 
                  ? '0 2px 6px rgba(148, 163, 184, 0.2)'
                  : `0 4px 12px ${primaryColor}4d`,
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 3,
                opacity: data.hasConnection ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!data.hasConnection) {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = `0 6px 16px ${primaryColor}66`;
                }
              }}
              onMouseLeave={(e) => {
                if (!data.hasConnection) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}4d`;
                }
              }}
            >
              <FaRegPlusSquare size={20} style={{ color: '#fff' }} />
            </button>

            {/* Dropdown Menu */}
            {showDropdown && !data.hasConnection && (
              <div style={{
                position: 'absolute',
                top: '100%',
                marginTop: '8px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e2e8f0',
                minWidth: '180px',
                zIndex: 1000,
                animation: 'slideUp 0.2s ease-out',
                overflow: 'hidden'
              }}>
                {/* Dropdown Arrow */}
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '12px',
                  width: '12px',
                  height: '12px',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderBottom: 'none',
                  borderRight: 'none',
                  transform: 'rotate(45deg)',
                  zIndex: -1
                }} />
                
                {/* Add Agent Option */}
                <button
                  onClick={handleAddAgent}
                  className='bg-0 border-0 p-3 w-100 d-flex align-items-center gap-3 cursor-pointer'
                  style={{
                    transition: 'all 0.15s ease',
                    borderBottom: '1px solid #f1f5f9'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    color: '#4880ff',
                    background: 'linear-gradient(135deg, #8D33FF 0%, #7c2ae6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <FaRobot size={14} style={{ color: '#fff' }} />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: '2px'
                    }}>
                      Add Agent
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#64748b'
                    }}>
                      Create new agent
                    </div>
                  </div>
                </button>

                {/* Add Task Option */}
                <button
                  onClick={handleAddTask}
                  className='bg-0 border-0 p-3 w-100 d-flex align-items-center gap-3 cursor-pointer'
                  style={{
                    transition: 'all 0.15s ease',
                    borderBottom: '1px solid #f1f5f9'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <TaskEngineIcon />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: '2px'
                    }}>
                      Add Task
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#64748b'
                    }}>
                      Create new task
                    </div>
                  </div>
                </button>

              </div>
            )}
          </div>
        </div>
        )}
        
        {/* Square plus icon for tool handle */}
        {!hasTool && <div style={{
          position: 'absolute',
          left: 98,
          bottom: -60,
          transform: 'translateX(-50%)',
          display: 'flex',  
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 2
        }}>
          <div style={{
            width: 2,
            height: 40,
            background: primaryColor
          }} />
          <FaRegPlusSquare 
            size={18} 
            onClick={handleAddTool} 
            style={{
              color: primaryColor, 
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.2)';
              e.currentTarget.style.color = '#b89d5a';
              e.currentTarget.style.filter = 'drop-shadow(0 2px 4px rgba(204, 177, 109, 0.4))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = primaryColor;
              e.currentTarget.style.filter = 'none';
            }}
          />
        </div>}
        
        {/* Delete button that appears on hover - top right corner */}
        {isHovered && (
          <div style={{ position: 'absolute', top: -10, right: -10, zIndex: 10 }}>
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#fff',
                border: '1px solid #6c757d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onClick={handleDelete}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.borderColor = '#495057';
                e.currentTarget.style.background = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.borderColor = '#6c757d';
                e.currentTarget.style.background = '#fff';
              }}
            >
              <i className="fas fa-trash" style={{ 
                color: '#6c757d', 
                fontSize: '9px',
                cursor: 'pointer'
              }}></i>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

